import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import { useAuth } from '../../context/AuthContext';
import { tenantAPI, membershipApplicationAPI, classAPI, amenityAPI, memberProfileAPI, classInstanceAPI, coachingProgramInstanceAPI, coachingSessionAPI, eventAPI, groupPlayAPI, auditLogAPI, transactionAPI } from '../../services/api';
import { MembershipApplication, User, ClassInstance, CoachingProgramInstance, Event, AuditLog, Transaction } from '../../types';
import { CoachingSession } from '../../types/coaching';
import { formatTimeString } from '../../utils/dateUtils';

interface ScheduleActivity {
  id: string;
  type: 'class' | 'coaching' | 'event' | 'groupplay';
  title: string;
  startTime: string;
  endTime: string;
  location: string;
  instructor: string;
  capacity: number;
  enrolled: number;
  level: string;
  sessionDate: string;
}

interface TenantDetails {
  id: string;
  name: string;
  subdomain: string;
  status: string;
}

interface MemberFailedPayments {
  memberId: string;
  memberName: string;
  membershipTier?: string;
  totalFailedAmount: number;
  failureCount: number;
  lastFailureDate: string;
  lastFailureReason: string;
  transactions: Transaction[];
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tenantInfo, setTenantInfo] = useState<TenantDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // States for pending member applications
  const [pendingApplications, setPendingApplications] = useState<MembershipApplication[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [loadingApplications, setLoadingApplications] = useState<boolean>(false);
  const [memberCount, setMemberCount] = useState<number>(0);
  const [classCount, setClassCount] = useState<number>(0);
  const [amenityBookingCount, setAmenityBookingCount] = useState<number>(0);
  const [todaySchedule, setTodaySchedule] = useState<ScheduleActivity[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState<boolean>(false);
  const [scheduleCurrentPage, setScheduleCurrentPage] = useState<number>(0);
  const scheduleItemsPerPage = 4;
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);
  const [loadingActivity, setLoadingActivity] = useState<boolean>(false);
  const [failedPayments, setFailedPayments] = useState<MemberFailedPayments[]>([]);
  const [failedPaymentsCount, setFailedPaymentsCount] = useState<number>(0);
  const [loadingFailedPayments, setLoadingFailedPayments] = useState<boolean>(false);

  // Dashboard state management
  // Check if super admin is viewing a specific tenant
  const isSuperAdminViewingTenant = user?.role === 'super_admin' && localStorage.getItem('tenantId');

  useEffect(() => {
    // Only fetch tenant info if super admin is viewing a specific tenant
    const fetchTenantInfo = async () => {
      if (isSuperAdminViewingTenant) {
        setLoading(true);
        const selectedTenantId = localStorage.getItem('tenantId');

        try {
          const response = await tenantAPI.getTenantById(selectedTenantId!);
          setTenantInfo(response.data);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching tenant info:', err);
          setError('Failed to load tenant information');
          setLoading(false);
        }
      }
    };

    fetchTenantInfo();
  }, [isSuperAdminViewingTenant]);
  // Fetch pending applications
  useEffect(() => {
    const fetchPendingApplications = async () => {
      if (!user?.tenantId && !isSuperAdminViewingTenant) return;

      setLoadingApplications(true);
      try {
        // Get pending applications count
        const countResponse = await membershipApplicationAPI.getPendingCount();
        setPendingCount(countResponse.data.count);

        // Get pending applications directly from the pending endpoint
        const applicationsResponse = await membershipApplicationAPI.getPendingApplications();

        // Get first 3 applications for the dashboard preview
        setPendingApplications(applicationsResponse.data.slice(0, 3));

        setLoadingApplications(false);
      } catch (err) {
        console.error('Error fetching pending applications:', err);
        setLoadingApplications(false);
      }
    };
    fetchPendingApplications();
  }, [user?.tenantId, isSuperAdminViewingTenant]); const handleBackToTenants = () => {
    localStorage.removeItem('tenantId');
    window.location.href = '/dashboard';
  };

  // Fetch number of members
  useEffect(() => {
    const fetchMemberCount = async () => {
      if (!user?.tenantId && !isSuperAdminViewingTenant) return;

      setLoading(true);
      try {
        // Fetch member count from the API
        const response = await memberProfileAPI.getMemberProfileCount({ status: 'active' } );
        let count = response.data.count;
        setMemberCount(count);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching member count:', err);
        setError('Failed to load member count');
        setLoading(false);
      }
    };

    fetchMemberCount();
  }, [user?.tenantId, isSuperAdminViewingTenant, tenantInfo]);
  // Fetch class count for this week
  useEffect(() => {
    const fetchClassCount = async () => {
      // Get start and end of current week
      const now = new Date();
      const first = now.getDate() - now.getDay(); // Sunday
      const last = first + 6; // Saturday
      const startDate = new Date(now.setDate(first));
      const endDate = new Date(now.setDate(last));
      const startStr = startDate.toISOString().slice(0, 10);
      const endStr = endDate.toISOString().slice(0, 10);
      try {
        const response = await classAPI.getClassCount(startStr, endStr);
        setClassCount(Array.isArray(response.data) ? response.data.length : 0);
      } catch (err) {
        setClassCount(0);
      }
    };
    fetchClassCount();
  }, [user?.tenantId, isSuperAdminViewingTenant, tenantInfo]);

  // Fetch amenity booking count for this week
  useEffect(() => {
    const fetchAmenityBookingCount = async () => {
      const now = new Date();
      const first = now.getDate() - now.getDay(); // Sunday
      const last = first + 6; // Saturday
      const startDate = new Date(now.setDate(first));
      const endDate = new Date(now.setDate(last));
      const startStr = startDate.toISOString().slice(0, 10);
      const endStr = endDate.toISOString().slice(0, 10);
      try {
        const response = await amenityAPI.getAllAmenityBookings({ startDate: startStr, endDate: endStr });
        setAmenityBookingCount(Array.isArray(response.data) ? response.data.length : 0);
      } catch (err) {
        setAmenityBookingCount(0);
      }
    };
    fetchAmenityBookingCount();
  }, [user?.tenantId, isSuperAdminViewingTenant, tenantInfo]);

  // Fetch today's schedule (classes, coaching programs, and events)
  useEffect(() => {
    const fetchTodaySchedule = async () => {
      if (!user?.tenantId && !isSuperAdminViewingTenant) return;

      setLoadingSchedule(true);
      try {
        const today = new Date().toISOString().slice(0, 10);
        // ** DEBUGGING LOGS **
        // console.log('Fetching today\'s schedule for date:', today);
        
        // Fetch all activity types for today
        const [classInstancesResponse, coachingInstancesResponse, coachingSessionsResponse, eventsResponse, groupPlayScheduleResponse] = await Promise.all([
          classInstanceAPI.getInstances({ sessionDate: today }).catch((err) => {
            console.error('Error fetching class instances:', err);
            return { data: [] };
          }),
          coachingProgramInstanceAPI.getInstances({ sessionDate: today }).catch((err) => {
            console.error('Error fetching coaching program instances:', err);
            return { data: [] };
          }),
          coachingSessionAPI.getCoachingSessions({ 
            startDate: today,
            endDate: today
          }).catch((err) => {
            console.error('Error fetching coaching sessions:', err);
            return { data: [] };
          }),
          eventAPI.getAll({ 
            upcoming: true,
            // Filter events for today on the frontend since API might not support date filtering
          }).catch((err) => {
            console.error('Error fetching events:', err);
            return { data: [] };
          }),
          groupPlayAPI.getGroupPlaySchedule({
            startDate: today,
            endDate: today
          }).catch((err) => {
            console.error('Error fetching group play schedule:', err);
            return { data: [] };
          })
        ]);


        // ** DEBUGGING LOGS **
        // console.log('API Responses:', {
        //   classInstances: classInstancesResponse.data?.length || 0,
        //   coachingInstances: coachingInstancesResponse.data?.length || 0,
        //   coachingSessions: coachingSessionsResponse.data?.length || 0,
        //   events: eventsResponse.data?.length || 0
        // });

        // Normalize all activities to a common format
        const activities: ScheduleActivity[] = [];

        // Add class instances (filter out cancelled/inactive)
        if (Array.isArray(classInstancesResponse.data)) {
          // ** DEBUGGING LOGS **
          // console.log('Processing', classInstancesResponse.data.length, 'class instances');
          classInstancesResponse.data
            .filter((instance: ClassInstance) => 
              instance.active !== false && 
              instance.status !== 'CANCELLED' && 
              instance.status !== 'cancelled'
            )
            .forEach((instance: ClassInstance) => {
              activities.push({
                id: instance.id,
                type: 'class',
                title: instance.name || 'Unnamed Class',
                startTime: instance.startTime || '',
                endTime: instance.endTime || '',
                location: instance.venue || '',
                instructor: instance.instructor || '',
                capacity: instance.capacity || 0,
                enrolled: instance.enrolledCount || 0,
                level: instance.level || '',
                sessionDate: instance.sessionDate
              });
            });
        }

        // Add coaching program instances (filter out cancelled/inactive)
        if (Array.isArray(coachingInstancesResponse.data)) {
          // ** DEBUGGING LOGS **
          // console.log('Processing', coachingInstancesResponse.data.length, 'coaching program instances');
          coachingInstancesResponse.data
            .filter((instance: CoachingProgramInstance) => 
              instance.active !== false && 
              instance.status !== 'CANCELLED' && 
              instance.status !== 'cancelled'
            )
            .forEach((instance: CoachingProgramInstance) => {
              activities.push({
                id: instance.id,
                type: 'coaching',
                title: instance.name || 'Unnamed Program',
                startTime: instance.startTime || '00:00:00',
                endTime: instance.endTime || '01:00:00',
                location: instance.venues && instance.venues.length > 0 ? `${instance.venues.length} venue(s)` : 'TBD',
                instructor: instance.coachName || 'TBD',
                capacity: instance.capacity || 0,
                enrolled: instance.enrolledCount || 0,
                level: instance.level || 'All Levels',
                sessionDate: instance.sessionDate
              });
            });
        }

        // Add coaching sessions (filter out cancelled/inactive)
        if (Array.isArray(coachingSessionsResponse.data)) {
          // ** DEBUGGING LOGS **
          // console.log('Processing', coachingSessionsResponse.data.length, 'coaching sessions');
          coachingSessionsResponse.data
            .filter((session: CoachingSession) => {
              // Filter for today's sessions and exclude cancelled/inactive
              const sessionDate = new Date(session.sessionDate).toISOString().slice(0, 10);
              return sessionDate === today && 
                     (session as any).active !== false && 
                     (session as any).status !== 'CANCELLED' && 
                     (session as any).status !== 'cancelled';
            })
            .forEach((session: CoachingSession) => {
              activities.push({
                id: session.id,
                type: 'coaching',
                title: session.name || 'Coaching Session',
                startTime: session.startTime || '00:00:00',
                endTime: session.endTime || '01:00:00',
                location: session.venues && session.venues.length > 0 ? `${session.venues.length} venue(s)` : 'TBD',
                instructor: session.coachName || 'TBD',
                capacity: session.capacity || 1,
                enrolled: 0, // Coaching sessions don't have enrolledCount in the basic interface
                level: session.level || 'All Levels',
                sessionDate: session.sessionDate
              });
            });
        }

        // Add events (filter for today and exclude cancelled/completed/draft)
        if (Array.isArray(eventsResponse.data)) {
          eventsResponse.data
            .filter((event: Event) => {
              const eventDate = new Date(event.eventDateTime).toISOString().slice(0, 10);
              return eventDate === today && 
                     event.status !== 'cancelled' && 
                     event.status !== 'completed' && 
                     event.status !== 'draft';
            })
            .forEach((event: Event) => {
              const eventDateTime = new Date(event.eventDateTime);
              const startTime = eventDateTime.toTimeString().slice(0, 8);
              // Assume 1 hour duration if no end time available
              const endDateTime = new Date(eventDateTime.getTime() + 60 * 60 * 1000);
              const endTime = endDateTime.toTimeString().slice(0, 8);
              
              activities.push({
                id: event.id,
                type: 'event',
                title: event.title,
                startTime: startTime,
                endTime: endTime,
                location: event.location || 'TBD',
                instructor: 'Event',
                capacity: event.maxRegistrants || 0,
                enrolled: event.registeredCount || 0,
                level: event.category || 'All Levels',
                sessionDate: today
              });
            });
        }

        // Add group play instances (the schedule API already excludes cancelled/inactive
        // instances and group plays; keep a cheap defensive filter)
        if (Array.isArray(groupPlayScheduleResponse.data)) {
          groupPlayScheduleResponse.data
            .filter((instance: any) =>
              instance.active !== false &&
              instance.status !== 'CANCELLED' &&
              instance.status !== 'cancelled'
            )
            .forEach((instance: any) => {
              activities.push({
                id: instance.id,
                type: 'groupplay',
                title: instance.name || 'Group Play',
                startTime: instance.startTime || '00:00:00',
                endTime: instance.endTime || '01:00:00',
                location: instance.venues && instance.venues.length > 0 ? `${instance.venues.length} venue(s)` : 'TBD',
                instructor: 'Group Play',
                capacity: instance.capacity || 0,
                enrolled: instance.confirmedCount || 0,
                level: instance.parentGroupPlay?.type || 'All Levels',
                sessionDate: today
              });
            });
        }

        // Sort activities by start time
        activities.sort((a, b) => a.startTime.localeCompare(b.startTime));
        
        setTodaySchedule(activities);
        setScheduleCurrentPage(0); // Reset pagination when data changes
        setLoadingSchedule(false);
      } catch (err) {
        console.error('Error fetching today\'s schedule:', err);
        setLoadingSchedule(false);
      }
    };

    fetchTodaySchedule();
  }, [user?.tenantId, isSuperAdminViewingTenant, tenantInfo]);

  // Fetch recent activity from audit logs
  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user?.tenantId && !isSuperAdminViewingTenant) return;

      setLoadingActivity(true);
      try {
        const response = await auditLogAPI.getAuditLogs({
          page: 1,
          limit: 5,
          action: 'membership_change',
          entityType: 'MemberProfile'
        });
        setRecentActivity(response.data.data || []);
        setLoadingActivity(false);
      } catch (err) {
        console.error('Error fetching recent activity:', err);
        setLoadingActivity(false);
      }
    };

    fetchRecentActivity();
  }, [user?.tenantId, isSuperAdminViewingTenant, tenantInfo]);

  // Fetch failed payments
  useEffect(() => {
    const fetchFailedPayments = async () => {
      if (!user?.tenantId && !isSuperAdminViewingTenant) return;

      setLoadingFailedPayments(true);
      try {
        const response = await transactionAPI.getAll({
          status: 'failed',
        });
        
        // Get all failed transactions
        const failedTxns = Array.isArray(response.data.transactions) 
          ? response.data.transactions 
          : Array.isArray(response.data) 
            ? response.data 
            : [];
        
        // Group by member and calculate totals
        const memberMap = new Map<string, MemberFailedPayments>();
        
        failedTxns.forEach((txn: Transaction) => {
          if (!memberMap.has(txn.memberId)) {
            memberMap.set(txn.memberId, {
              memberId: txn.memberId,
              memberName: txn.memberProfile 
                ? `${txn.memberProfile.firstName} ${txn.memberProfile.lastName}`
                : 'Unknown Member',
              totalFailedAmount: 0,
              failureCount: 0,
              lastFailureDate: txn.metadata?.lastErrorAt || txn.updatedAt || '',
              lastFailureReason: txn.metadata?.lastError || 'Payment failed',
              transactions: []
            });
          }
          
          const memberData = memberMap.get(txn.memberId)!;
          memberData.totalFailedAmount += Math.round(Number(txn.totalAmount) * 100) / 100; // Avoid floating point issues
          memberData.failureCount += 1;
          memberData.transactions.push(txn);
          
          // Update to most recent failure
          const currentFailureDate = txn.metadata?.lastErrorAt || txn.updatedAt || '';
          if (currentFailureDate > memberData.lastFailureDate) {
            memberData.lastFailureDate = currentFailureDate;
            memberData.lastFailureReason = txn.metadata?.lastError || 'Payment failed';
          }
        });
        
        // Convert to array and sort by most recent failure
        const aggregatedFailures = Array.from(memberMap.values())
          .sort((a, b) => new Date(b.lastFailureDate).getTime() - new Date(a.lastFailureDate).getTime());
        
        setFailedPaymentsCount(aggregatedFailures.length);
        // Show only first 3 members for dashboard preview
        setFailedPayments(aggregatedFailures.slice(0, 3));
        setLoadingFailedPayments(false);
      } catch (err) {
        console.error('Error fetching failed payments:', err);
        setLoadingFailedPayments(false);
      }
    };

    fetchFailedPayments();
  }, [user?.tenantId, isSuperAdminViewingTenant, tenantInfo]);

  // Navigation for reviewing application details
  const handleReviewApplication = (applicationId: string) => {
    window.location.href = `/membership-applications/${applicationId}`;
  };

  // Navigation to member profile
  const handleViewMemberProfile = (memberProfileId: string) => {
    window.location.href = `/member-profiles/${memberProfileId}`;
  };

  // Refresh recent activity only
  const handleRefreshRecentActivity = async () => {
    setLoadingActivity(true);
    try {
      const response = await auditLogAPI.getAuditLogs({
        page: 1,
        limit: 5,
        action: 'membership_change',
        entityType: 'MemberProfile'
      });
      setRecentActivity(response.data.data || []);
    } catch (err) {
      console.error('Error refreshing recent activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };

  return (
    <Container>
      {isSuperAdminViewingTenant && (
        <Box sx={{ mt: 2, mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBackToTenants();
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Super Admin
            </Link>
            <Link
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
              color="inherit"
              href="/clubs"
              onClick={(e) => {
                e.preventDefault();
                handleBackToTenants();
                window.location.href = '/clubs';
              }}
            >
              All Clubs
            </Link>
            <Typography color="text.primary">
              {tenantInfo?.name || "Club Dashboard"}
            </Typography>
          </Breadcrumbs>
        </Box>
      )}

      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom component="div">
          {isSuperAdminViewingTenant && tenantInfo
            ? `${tenantInfo.name} - Admin Dashboard`
            : 'Admin Dashboard'}
        </Typography>

        {loading ? (
          <CircularProgress size={20} sx={{ ml: 1 }} />
        ) : isSuperAdminViewingTenant && tenantInfo ? (
          <Typography variant="subtitle1" gutterBottom>
            You are viewing this club as a Super Administrator
            {tenantInfo.status !== 'active' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This club is currently {tenantInfo.status.toUpperCase()}
              </Alert>
            )}
          </Typography>
        ) : (
          <Typography variant="subtitle1" gutterBottom>
            {user?.tenantId ? 'Club Administrator' : 'Super Administrator'}
          </Typography>
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </Box>

      <Grid container spacing={3}>        {/* Overview Stats */}
        <Grid size={{ xs: 12, md: 3 }} >
          <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="h4" color="primary">{memberCount}</Typography>
            <Typography variant="body1">Active Members</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} >
          <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            {loadingApplications ? (
              <CircularProgress size={30} sx={{ my: 1 }} />
            ) : (
              <Typography variant="h4" color="primary">{pendingCount}</Typography>
            )}
            <Typography variant="body1">Pending Approvals</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} >
          <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="h4" color="primary">{classCount}</Typography>
            <Typography variant="body1">Classes This Week</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} >
          <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="h4" color="primary">{amenityBookingCount}</Typography>
            <Typography variant="body1">Amenity Bookings</Typography>
          </Paper>
        </Grid>        {/* Pending Approvals */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Pending Member Approvals
            </Typography>
            {loadingApplications ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={30} />
              </Box>
            ) : pendingApplications.length > 0 ? (
              <List>
                {pendingApplications.map((application, index) => (
                  <React.Fragment key={application.id}>
                    {index > 0 && <Divider />}                    <ListItem>
                      <ListItemText
                        primary={`${application.firstName} ${application.lastName}`}
                        secondary={`Submitted: ${new Date(application.createdAt).toLocaleDateString()} • ${application.membershipTierName}`}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleReviewApplication(application.id)}
                      >
                        Review
                      </Button>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No pending applications
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="text"
                component={Link}
                href="/membership-applications"
                disabled={pendingCount === 0}
              >
                {pendingCount > 0
                  ? `View All Pending Approvals (${pendingCount})`
                  : 'No Pending Approvals'
                }
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 0 }}>
                Recent Activity
              </Typography>
              <IconButton 
                onClick={handleRefreshRecentActivity}
                disabled={loadingActivity}
                size="small"
                title="Refresh recent activity"
              >
                {loadingActivity ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Box>
            {loadingActivity ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={30} />
              </Box>
            ) : recentActivity.length > 0 ? (
              <List>
                {recentActivity.map((log, index) => {
                  const logDate = new Date(log.createdAt);
                  const now = new Date();
                  const isToday = logDate.toDateString() === now.toDateString();
                  const isYesterday = logDate.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString();
                  
                  let timeDisplay = '';
                  if (isToday) {
                    timeDisplay = `Today, ${logDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                  } else if (isYesterday) {
                    timeDisplay = `Yesterday, ${logDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                  } else {
                    timeDisplay = logDate.toLocaleString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      hour: 'numeric', 
                      minute: '2-digit', 
                      hour12: true 
                    });
                  }

                  return (
                    <React.Fragment key={log.id}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={log.description || `${log.action} - ${log.entityType}`}
                          secondary={`${log.userFullName || log.userEmail} • ${timeDisplay}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No recent activity
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button variant="text" component={Link} href="/audit-logs">
                View All Activity
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Failed Payments */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Failed Payments
            </Typography>
            {loadingFailedPayments ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={30} />
              </Box>
            ) : failedPayments.length > 0 ? (
              <List>
                {failedPayments.map((memberFailure, index) => {
                  const failureDate = memberFailure.lastFailureDate
                    ? new Date(memberFailure.lastFailureDate).toLocaleDateString()
                    : 'N/A';
                  
                  return (
                    <React.Fragment key={memberFailure.memberId}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleViewMemberProfile(memberFailure.memberId)}
                              sx={{ 
                                textAlign: 'left',
                                fontWeight: 500,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {memberFailure.memberName}
                            </Link>
                          }
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="error">
                                ${memberFailure?.totalFailedAmount?.toFixed(2)} failed ({memberFailure.failureCount} transaction{memberFailure.failureCount > 1 ? 's' : ''})
                              </Typography>
                              <br />
                              <Typography component="span" variant="caption" color="text.secondary">
                                {memberFailure.lastFailureReason}
                              </Typography>
                              <br />
                              <Typography component="span" variant="caption" color="text.secondary">
                                Last failure: {failureDate}
                              </Typography>
                            </>
                          }
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No failed payments
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="text"
                component={Link}
                href="/arrears-report"
                disabled={failedPaymentsCount === 0}
              >
                {failedPaymentsCount > 0
                  ? `View Members in Arrears Report (${failedPaymentsCount} member${failedPaymentsCount > 1 ? 's' : ''})`
                  : 'No Failed Payments'
                }
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Member Account Balance (placeholder for future) */}
        {/* <Grid size={{ xs: 12, md: 6 }} >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ py: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: 1 }}
                onClick={() => window.location.href = '/transactions'}
              >
                View All Transactions
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: 1 }}
                onClick={() => window.location.href = '/member-profiles'}
              >
                Manage Members
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.location.href = '/reports'}
              >
                View Reports
              </Button>
            </Box>
          </Paper>
        </Grid> */}

        {/* Today's Schedule */}
        <Grid size={{ xs: 12 }} >
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Today's Schedule
              </Typography>
              {todaySchedule.length > scheduleItemsPerPage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton 
                    onClick={() => setScheduleCurrentPage(Math.max(0, scheduleCurrentPage - 1))}
                    disabled={scheduleCurrentPage === 0}
                    size="small"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {Math.min(scheduleCurrentPage * scheduleItemsPerPage + 1, todaySchedule.length)} - {Math.min((scheduleCurrentPage + 1) * scheduleItemsPerPage, todaySchedule.length)} of {todaySchedule.length}
                  </Typography>
                  <IconButton 
                    onClick={() => setScheduleCurrentPage(Math.min(Math.ceil(todaySchedule.length / scheduleItemsPerPage) - 1, scheduleCurrentPage + 1))}
                    disabled={scheduleCurrentPage >= Math.ceil(todaySchedule.length / scheduleItemsPerPage) - 1}
                    size="small"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
            {loadingSchedule ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={30} />
              </Box>
            ) : todaySchedule.length > 0 ? (
              <Grid container spacing={2}>
                {todaySchedule.slice(scheduleCurrentPage * scheduleItemsPerPage, (scheduleCurrentPage + 1) * scheduleItemsPerPage).map((activity, index) => {
                  const getActivityColor = (type: string) => {
                    switch (type) {
                      case 'class': return 'primary.main';
                      case 'coaching': return 'secondary.main';
                      case 'event': return 'warning.main';
                      case 'groupplay': return 'success.main';
                      default: return 'text.primary';
                    }
                  };

                  const getCapacityColor = (enrolled: number, capacity: number) => {
                    if (capacity === 0) return 'text.secondary';
                    const percentage = (enrolled / capacity) * 100;
                    if (percentage >= 100) return 'error.main';
                    if (percentage >= 80) return 'warning.main';
                    return 'success.main';
                  };

                  // Use the centralized time formatting utility
                  const formatTime = (timeStr: string) => {
                    if (!timeStr) return 'TBD';
                    const formatted = formatTimeString(timeStr);
                    return formatted || timeStr; // Fallback to original if formatting fails
                  };

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={activity.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {formatTime(activity.startTime)} - {formatTime(activity.endTime)}
                          </Typography>
                          <Typography variant="body1" sx={{ color: getActivityColor(activity.type) }}>
                            {activity.title || 'Untitled Activity'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {activity.location}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {activity.type === 'event' ? 'Event' : `Instructor: ${activity.instructor}`}
                          </Typography>
                          <Typography variant="body2" sx={{ color: getCapacityColor(activity.enrolled, activity.capacity) }}>
                            {activity.capacity > 0 
                              ? `${activity.enrolled}/${activity.capacity} Participants`
                              : `${activity.enrolled} Registered`
                            }
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'inline-block',
                              mt: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: activity.type === 'class' ? 'primary.light' : 
                                       activity.type === 'coaching' ? 'secondary.light' : 
                                       activity.type === 'event' ? 'warning.light' : 
                                       activity.type === 'groupplay' ? 'success.light' : 'grey.400',
                              color: 'white',
                              textTransform: 'capitalize'
                            }}
                          >
                            {activity.type === 'groupplay' ? 'Group Play' : activity.type}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No activities scheduled for today
                </Typography>
              </Box>
            )}
            {/* {todaySchedule.length > scheduleItemsPerPage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min(scheduleCurrentPage * scheduleItemsPerPage + 1, todaySchedule.length)} - {Math.min((scheduleCurrentPage + 1) * scheduleItemsPerPage, todaySchedule.length)} of {todaySchedule.length} activities
                </Typography>
              </Box>
            )} */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button variant="text" onClick={() => window.location.href = '/calendar'}>
                View Full Schedule
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>      {/* End of Dashboard Content */}
    </Container>
  );
};

export default AdminDashboard;
